import { useEffect, useRef } from "react";

export interface AddressBar {
  href: () => string;
  state: () => unknown;
  replace: (state: unknown, href: string) => void;
  title: () => string;
  setTitle: (title: string) => void;
  onLeave: (listener: () => void) => () => void;
}

export const browserAddressBar: AddressBar = {
  href: () => location.href,
  state: () => history.state as unknown,
  replace: (state, href) => history.replaceState(state, "", href),
  title: () => document.title,
  setTitle: (title) => {
    document.title = title;
  },
  onLeave: (listener) => {
    addEventListener("popstate", listener);
    return () => removeEventListener("popstate", listener);
  },
};

export interface AddressEntry {
  href: string;
  /** What the browser's bookmark shortcut names the bookmark. */
  title: string;
}

/**
 * Shows `entry` in the address bar and tab title while it is set, so the
 * browser's own bookmark shortcut saves exactly it, and puts the page's
 * URL and title back on cleanup. The router's history state rides along
 * untouched, so a reload lands on the entry and Back still works. Back
 * itself leaves the entry: the restore only runs while the bar still shows
 * it, so the page Back reached is never overwritten, and `onLeave` tells
 * the caller to close. `onLeave` is read through a ref, so a caller
 * passing a fresh function each render never re-swaps.
 */
export function useAddressSwap(
  entry: AddressEntry | null,
  onLeave: () => void,
  bar: AddressBar = browserAddressBar,
): void {
  const leave = useRef(onLeave);
  useEffect(() => {
    leave.current = onLeave;
  }, [onLeave]);

  const href = entry?.href ?? null;
  const title = entry?.title ?? null;
  useEffect(() => {
    if (href === null || title === null) return;
    const original = {
      href: bar.href(),
      title: bar.title(),
      state: bar.state(),
    };
    bar.replace(original.state, href);
    bar.setTitle(title);
    const stopListening = bar.onLeave(() => leave.current());
    return () => {
      stopListening();
      if (bar.href() !== href) return;
      bar.replace(original.state, original.href);
      bar.setTitle(original.title);
    };
  }, [href, title, bar]);
}
